/**
 * SiteSearchOverlay — contextual, friction-free search palette.
 *
 * Cmd/Ctrl+K opens a full-screen palette. Users type once and see live
 * results across every SEO surface we own: 106+ Canadian cities, all 13
 * provinces (both the property-management page and the Residential
 * Tenancy Act alias), competitor comparisons, feature landings, tools,
 * and the 2026 stats report.
 *
 * The index is a small static array so search is instant, offline-safe,
 * and never blocks first paint. No API round-trip.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, X, MapPin, FileText, GitCompareArrows, Wrench } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// STATIC SEARCH INDEX
// Every group is (label, path, kind, keywords). `keywords` is a flat
// string used for substring matching; kept lower-case at build time.
// ─────────────────────────────────────────────────────────────────────
const CITY_ROWS = [
  ["Toronto, ON",         "toronto"],       ["Ottawa, ON",         "ottawa"],
  ["Mississauga, ON",     "mississauga"],   ["Brampton, ON",       "brampton"],
  ["Hamilton, ON",        "hamilton"],      ["London, ON",         "london"],
  ["Kitchener, ON",       "kitchener"],     ["Windsor, ON",        "windsor"],
  ["Barrie, ON",          "barrie"],        ["Guelph, ON",         "guelph"],
  ["Kingston, ON",        "kingston"],      ["Peterborough, ON",   "peterborough"],
  ["Belleville, ON",      "belleville"],    ["Sudbury, ON",        "sudbury"],
  ["Thunder Bay, ON",     "thunder-bay"],   ["Sault Ste. Marie, ON","sault-ste-marie"],
  ["North Bay, ON",       "north-bay"],     ["Sarnia, ON",         "sarnia"],
  ["St. Catharines, ON",  "st-catharines"], ["Oshawa, ON",         "oshawa"],
  ["Cornwall, ON",        "cornwall"],      ["Orillia, ON",        "orillia"],
  ["Timmins, ON",         "timmins"],
  ["Vancouver, BC",       "vancouver"],     ["Victoria, BC",       "victoria"],
  ["Surrey, BC",          "surrey"],        ["Burnaby, BC",        "burnaby"],
  ["Kelowna, BC",         "kelowna"],       ["Kamloops, BC",       "kamloops"],
  ["Nanaimo, BC",         "nanaimo"],       ["Chilliwack, BC",     "chilliwack"],
  ["Prince George, BC",   "prince-george"], ["Cranbrook, BC",      "cranbrook"],
  ["Abbotsford, BC",      "abbotsford"],    ["Penticton, BC",      "penticton"],
  ["Vernon, BC",          "vernon"],        ["Courtenay, BC",      "courtenay"],
  ["Fort St. John, BC",   "fort-st-john"],
  ["Calgary, AB",         "calgary"],       ["Edmonton, AB",       "edmonton"],
  ["Red Deer, AB",        "red-deer"],      ["Lethbridge, AB",     "lethbridge"],
  ["Airdrie, AB",         "airdrie"],       ["Spruce Grove, AB",   "spruce-grove"],
  ["Grande Prairie, AB",  "grande-prairie"],["Fort McMurray, AB",  "fort-mcmurray"],
  ["Medicine Hat, AB",    "medicine-hat"],  ["Camrose, AB",        "camrose"],
  ["Leduc, AB",           "leduc"],         ["St. Albert, AB",     "st-albert"],
  ["Sherwood Park, AB",   "sherwood-park"], ["Cold Lake, AB",      "cold-lake"],
  ["Montréal, QC",        "montreal"],      ["Québec City, QC",    "quebec-city"],
  ["Laval, QC",           "laval"],         ["Gatineau, QC",       "gatineau"],
  ["Sherbrooke, QC",      "sherbrooke"],    ["Trois-Rivières, QC", "trois-rivieres"],
  ["Saguenay, QC",        "saguenay"],      ["Lévis, QC",          "levis"],
  ["Drummondville, QC",   "drummondville"], ["Rimouski, QC",       "rimouski"],
  ["Saint-Jérôme, QC",    "saint-jerome"],  ["Granby, QC",         "granby"],
  ["Winnipeg, MB",        "winnipeg"],      ["Brandon, MB",        "brandon"],
  ["Steinbach, MB",       "steinbach"],     ["Portage la Prairie, MB","portage-la-prairie"],
  ["Thompson, MB",        "thompson"],      ["Winkler, MB",        "winkler"],
  ["Morden, MB",          "morden"],        ["Selkirk, MB",        "selkirk"],
  ["Dauphin, MB",         "dauphin"],       ["Flin Flon, MB",      "flin-flon"],
  ["Saskatoon, SK",       "saskatoon"],     ["Regina, SK",         "regina"],
  ["Prince Albert, SK",   "prince-albert"], ["Moose Jaw, SK",      "moose-jaw"],
  ["Swift Current, SK",   "swift-current"], ["Yorkton, SK",        "yorkton"],
  ["North Battleford, SK","north-battleford"],["Estevan, SK",      "estevan"],
  ["Weyburn, SK",         "weyburn"],
  ["Halifax, NS",         "halifax"],       ["Sydney, NS",         "sydney"],
  ["Truro, NS",           "truro"],         ["New Glasgow, NS",    "new-glasgow"],
  ["Kentville, NS",       "kentville"],     ["Yarmouth, NS",       "yarmouth"],
  ["Moncton, NB",         "moncton"],       ["Fredericton, NB",    "fredericton"],
  ["Saint John, NB",      "saint-john"],    ["Bathurst, NB",       "bathurst"],
  ["Miramichi, NB",       "miramichi"],     ["Edmundston, NB",     "edmundston"],
  ["St. John's, NL",      "st-johns"],      ["Corner Brook, NL",   "corner-brook"],
  ["Gander, NL",          "gander"],        ["Grand Falls-Windsor, NL","grand-falls-windsor"],
  ["Mount Pearl, NL",     "mount-pearl"],
  ["Charlottetown, PE",   "charlottetown"], ["Summerside, PE",     "summerside"],
  ["Stratford, PE",       "stratford-pe"],
  ["Yellowknife, NT",     "yellowknife"],   ["Whitehorse, YT",     "whitehorse"],
  ["Iqaluit, NU",         "iqaluit"],
];

const PROVINCE_ROWS = [
  ["Ontario",                   "ontario"],
  ["British Columbia",          "british-columbia"],
  ["Alberta",                   "alberta"],
  ["Québec",                    "quebec"],
  ["Manitoba",                  "manitoba"],
  ["Saskatchewan",              "saskatchewan"],
  ["Nova Scotia",               "nova-scotia"],
  ["New Brunswick",             "new-brunswick"],
  ["Newfoundland and Labrador", "newfoundland-and-labrador"],
  ["Prince Edward Island",      "prince-edward-island"],
  ["Yukon",                     "yukon"],
  ["Northwest Territories",     "northwest-territories"],
  ["Nunavut",                   "nunavut"],
];

const COMPETITOR_ROWS = [
  ["Buildium", "buildium"], ["AppFolio", "appfolio"], ["DoorLoop", "doorloop"],
  ["Yardi Breeze", "yardi-breeze"], ["TurboTenant", "turbotenant"],
  ["RentSync", "rentsync"], ["TenantCloud", "tenantcloud"], ["Avail", "avail"],
  ["Innago", "innago"], ["RentRedi", "rentredi"], ["Rentec Direct", "rentec-direct"],
  ["Rent Manager", "rent-manager"], ["Propertyware", "propertyware"],
  ["SimplifyEm", "simplifyem"], ["Hemlane", "hemlane"], ["Stessa", "stessa"],
  ["Property Copilot", "property-copilot"], ["Haletale", "haletale"],
  ["Mi Property Portal", "mi-property-portal"], ["SingleKey", "singlekey"],
  ["Rentals.ca", "rentals-ca"], ["Realtor.ca", "realtor-ca"], ["Apartments.com", "apartments-com"],
];

// Static, hand-curated non-city surfaces that most searchers want.
const CORE_ROWS = [
  { label: "Free Rental Invoice Generator", path: "/tools/invoice-generator", kind: "tool", kw: "invoice bill gst hst pdf receipt" },
  { label: "Free Cold Email Builder for Landlords", path: "/tools/cold-email-builder", kind: "tool", kw: "email template late rent renewal notice" },
  { label: "Rent Increase Notice Generator (N1 / RTB / TAL)", path: "/tools/rent-increase-notice", kind: "tool", kw: "n1 rtb tal rent increase notice cap" },
  { label: "N4 Late-Rent Notice Generator (Ontario)", path: "/tools/n4-late-rent-notice", kind: "tool", kw: "n4 late rent notice ontario ltb arrears" },
  { label: "Rent Affordability Calculator", path: "/tools/rent-affordability", kind: "tool", kw: "affordability rent income calculator budget" },
  { label: "ROI Calculator (Cap-Rate, Cash-on-Cash)", path: "/tools/roi-calculator", kind: "tool", kw: "roi cap rate cash on cash investment yield" },
  { label: "Canadian Landlord Statistics 2026 (Report)", path: "/reports/canadian-landlord-statistics-2026", kind: "report", kw: "statistics report data rent arrears vacancy landlord" },
  { label: "Best Property Management Software in Canada 2026", path: "/best-property-management-software-canada", kind: "landing", kw: "best top ranking canada property management software listicle" },
  { label: "Property Management Software for Small Landlords", path: "/small-landlord-software", kind: "landing", kw: "small landlord solo 1 unit 10 units diy" },
  { label: "PMC Platform for Property Management Companies (100-1000+ doors)", path: "/property-management-companies", kind: "landing", kw: "pmc property management company 500 doors enterprise scale" },
  { label: "Compare: Canadian Property Management Software Alternatives", path: "/alternatives", kind: "compare", kw: "alternatives buildium appfolio yardi rentsync doorloop turbotenant compare matrix table" },
  { label: "Editorial policy · Central Rentals", path: "/editorial-policy", kind: "landing", kw: "editorial policy corrections ethics eeat trust masthead" },
  { label: "Expert Insights (Canadian Landlord Playbooks)", path: "/insights", kind: "landing", kw: "insights articles blog daily expert landlord canada" },
  { label: "All Tools & Calculators", path: "/tools", kind: "landing", kw: "tools calculators generators all" },
  { label: "Answers Library (Canadian Rental FAQ)", path: "/answers", kind: "landing", kw: "answers faq questions canadian rental" },
];

// Build the full flat index once (module scope).
const INDEX = [
  ...CITY_ROWS.map(([label, slug]) => ({
    label: `Property management in ${label}`,
    path: `/landlord-software/city/${slug}`,
    kind: "city",
    kw: `${label.toLowerCase()} ${slug} property management landlord software rental`,
  })),
  ...PROVINCE_ROWS.map(([label, slug]) => ({
    label: `${label} — Property Management + Tenancy Act`,
    path: `/property-management/${slug}`,
    kind: "province",
    kw: `${label.toLowerCase()} ${slug} residential tenancy act rta rtb tal ltb notice n4 n12`,
  })),
  ...PROVINCE_ROWS.map(([label, slug]) => ({
    label: `${label} Residential Tenancy Act`,
    path: `/tenancy-act/${slug}`,
    kind: "province",
    kw: `${label.toLowerCase()} tenancy act rta landlord tenant law`,
  })),
  ...COMPETITOR_ROWS.map(([label, slug]) => ({
    label: `Central Rentals vs ${label}`,
    path: `/vs/${slug}`,
    kind: "compare",
    kw: `${label.toLowerCase()} alternative comparison versus ${slug}`,
  })),
  ...CORE_ROWS,
].map((row) => ({ ...row, kw: `${row.label.toLowerCase()} ${row.kw || ""}` }));

const KIND_ICON = { city: MapPin, province: FileText, compare: GitCompareArrows, tool: Wrench, report: FileText, landing: FileText };
const KIND_LABEL = { city: "City", province: "Province", compare: "Compare", tool: "Free tool", report: "Report", landing: "Page" };

// ─────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────
export default function SiteSearchOverlay() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global Cmd+K / Ctrl+K + `/` hotkeys.
  useEffect(() => {
    const onKey = (e) => {
      const isOpen = e.key === "k" && (e.metaKey || e.ctrlKey);
      const isSlash = e.key === "/" && !e.metaKey && !e.ctrlKey && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA";
      if (isOpen || isSlash) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Autofocus + reset when overlay opens.
  useEffect(() => {
    if (open) {
      setQ("");
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Results — simple substring scoring with a nudge for prefix matches.
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) {
      // Empty query — show the 10 most useful entry points.
      return INDEX.filter((r) => r.kind === "tool" || r.kind === "report" || r.kind === "landing").slice(0, 10);
    }
    const scored = [];
    for (const row of INDEX) {
      const idx = row.kw.indexOf(needle);
      if (idx === -1) continue;
      // Prefix match on the label wins > mid-string kw match.
      const labelHit = row.label.toLowerCase().indexOf(needle);
      const score = (labelHit === 0 ? 0 : 1000) + (labelHit >= 0 ? labelHit : 5000) + idx;
      scored.push({ row, score });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 12).map((s) => s.row);
  }, [q]);

  // Reset cursor when result set changes.
  useEffect(() => { setCursor(0); }, [q]);

  function go(row) {
    if (!row) return;
    setOpen(false);
    navigate(row.path);
  }

  function onInputKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(results.length - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[cursor]); }
  }

  return (
    <>
      {/* Trigger button — visible in the public header.
          - lg-and-above: compact icon+"Search" pill so it never
            starves the nav or the sign-up CTA of space on 1280-1600px.
          - xl-and-above: shows the long descriptor ("cities, tools…").
          - md–lg: icon-only compact pill to prevent the header overlap
            reported in the Feb 2026 UX review (logo colliding with nav). */}
      <button type="button" onClick={() => setOpen(true)}
        data-testid="site-search-trigger"
        aria-label="Search Central Rentals"
        className="hidden md:inline-flex items-center gap-2 pl-3 pr-3 py-1.5 rounded-full border border-slate-300 bg-white hover:border-emerald-500 hover:bg-emerald-50/40 text-slate-500 hover:text-slate-800 text-sm transition-colors">
        <Search className="w-4 h-4"/>
        <span className="hidden xl:inline whitespace-nowrap">Search cities, tools, tenancy&nbsp;acts…</span>
        <span className="hidden lg:inline xl:hidden whitespace-nowrap">Search</span>
      </button>
      {/* Mobile — matches the hamburger-card styling right beside it
          so the header reads as ONE cohesive control cluster instead
          of a floating half-ring (Feb 2026 mobile UX review). */}
      <button type="button" onClick={() => setOpen(true)}
        aria-label="Search Central Rentals"
        data-testid="site-search-trigger-mobile"
        className="md:hidden hamburger-card"
        style={{ padding: "10px" }}>
        <Search className="w-5 h-5 text-slate-900"/>
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-[10vh]"
          onClick={() => setOpen(false)}
          data-testid="site-search-overlay"
          style={{
            background: "radial-gradient(1200px 400px at 50% 0%, rgba(16,185,129,0.15) 0%, transparent 60%), rgba(2,6,23,0.72)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}>
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "linear-gradient(180deg, rgba(15,23,42,0.94) 0%, rgba(15,23,42,0.98) 100%)",
              color: "#e2e8f0",
              border: "1px solid rgba(148,163,184,0.18)",
              boxShadow: "0 30px 80px -20px rgba(2,6,23,0.75)",
            }}
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
              <Search className="w-5 h-5 flex-none" style={{ color: "#6ee7b7" }}/>
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
                type="text"
                placeholder="Search: Toronto, Winnipeg, Ontario RTA, N4, invoice generator, insights…"
                data-testid="site-search-input"
                className="flex-1 outline-none text-base bg-transparent placeholder:text-slate-500 text-slate-100"/>
              <button type="button" onClick={() => setOpen(false)}
                data-testid="site-search-close"
                aria-label="Close search"
                className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/10">
                <X className="w-4 h-4"/>
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="site-search-results">
              {results.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-slate-400">
                  Nothing matched "{q}". Try a Canadian city, a province, "N4", "invoice", "insights" or "tenancy act".
                </div>
              )}
              {results.map((row, i) => {
                const Icon = KIND_ICON[row.kind] || FileText;
                const active = i === cursor;
                return (
                  <button key={row.path} type="button"
                    onClick={() => go(row)}
                    onMouseEnter={() => setCursor(i)}
                    data-testid={`site-search-result-${i}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                    style={{ background: active ? "rgba(16,185,129,0.12)" : "transparent" }}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-none"
                      style={{
                        background: active ? "rgba(16,185,129,0.25)" : "rgba(148,163,184,0.12)",
                        color: active ? "#6ee7b7" : "#cbd5e1",
                      }}>
                      <Icon className="w-4 h-4"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-100 truncate">{row.label}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{KIND_LABEL[row.kind] || "Page"} · {row.path}</div>
                    </div>
                    <ArrowRight className={`w-4 h-4 flex-none`} style={{ color: active ? "#6ee7b7" : "#64748b" }}/>
                  </button>
                );
              })}
            </div>

            <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-400">
              <div className="flex items-center gap-3">
                <span><kbd className="px-1 h-4 border border-white/20 rounded bg-white/5 font-mono">↑↓</kbd> navigate</span>
                <span><kbd className="px-1 h-4 border border-white/20 rounded bg-white/5 font-mono">↵</kbd> open</span>
                <span><kbd className="px-1 h-4 border border-white/20 rounded bg-white/5 font-mono">esc</kbd> close</span>
              </div>
              <span>{results.length} match{results.length === 1 ? "" : "es"}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
